import { Component } from '@theme/component';
import { mediaQueryLarge } from '@theme/utilities';

/**
 * A footer information category whose link list expands and collapses.
 * The list starts open on large screens and collapsed on small screens.
 *
 * @typedef {object} FooterInfoCategoryRefs
 * @property {HTMLUListElement} panel - The list of links.
 *
 * @extends {Component<FooterInfoCategoryRefs>}
 */
class FooterInfoCategoryComponent extends Component {
  requiredRefs = ['panel'];

  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    super.connectedCallback();

    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#setExpanded(mediaQueryLarge.matches);
    mediaQueryLarge.addEventListener('change', (event) => this.#setExpanded(event.matches), { signal });
    // Open the list when a merchant selects one of its links in the theme editor.
    this.addEventListener('shopify:block:select', () => this.#setExpanded(true), { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  get expanded() {
    return this.hasAttribute('data-expanded');
  }

  /**
   * Toggles the link list.
   */
  toggle() {
    this.#setExpanded(!this.expanded);
  }

  /**
   * @param {boolean} expanded
   */
  #setExpanded(expanded) {
    this.toggleAttribute('data-expanded', expanded);
    this.refs.panel.hidden = !expanded;

    for (const control of this.querySelectorAll('[aria-controls]')) {
      control.setAttribute('aria-expanded', String(expanded));
    }
  }
}

if (!customElements.get('footer-info-category-component')) {
  customElements.define('footer-info-category-component', FooterInfoCategoryComponent);
}
